// The search at the top of a settings screen (spec/13 §13, 2026-10-07). It finds a setting by its label, its help, the
// words of its choices and the usual synonyms (`search.ts`: "pay as you go" finds PAYG, "pen drive" finds USB), and
// choosing one opens its group with the row in view, highlighted — the Advanced card or a collapsed dependant opened
// for it (`SettingsForm`'s `reveal`). The command palette (Ctrl K) searches every screen the member can reach; this
// one searches the screen they are on.
//
// A combobox over a listbox: arrow keys move through the results, Enter opens one, Escape clears the box.

import { useTranslation } from '@clavian/i18n/react';
import { useId, useMemo, useState, type KeyboardEvent, type ReactNode } from 'react';

import { SearchIcon } from '../shell/nav-icons';
import { placementOf } from './layout';
import { rankSettings, type SettingDoc } from './search';

export function SettingsSearchBox({ docs, onChoose }: { readonly docs: readonly SettingDoc[]; readonly onChoose: (doc: SettingDoc) => void }): ReactNode {
  const { t } = useTranslation('console');
  const id = useId();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const results = useMemo(() => rankSettings(docs, query, 8), [docs, query]);
  const searching = query.trim().length >= 2;
  const open = searching && results.length > 0;
  const listId = `${id}-results`;
  const optionId = (index: number) => `${id}-option-${index}`;

  const choose = (doc: SettingDoc | undefined) => {
    if (doc === undefined) return;
    onChoose(doc);
    setQuery('');
    setActive(0);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' && open) {
      event.preventDefault();
      setActive((current) => (current + 1) % results.length);
    } else if (event.key === 'ArrowUp' && open) {
      event.preventDefault();
      setActive((current) => (current - 1 + results.length) % results.length);
    } else if (event.key === 'Enter' && open) {
      event.preventDefault();
      choose(results[active]);
    } else if (event.key === 'Escape') {
      setQuery('');
      setActive(0);
    }
  };

  const sectionOf = (doc: SettingDoc): string => {
    const section = doc.key.startsWith('panel.') ? doc.key.slice('panel.'.length) : doc.key.includes('.') ? placementOf(doc.key).section : 'profile';
    return t(`settings.section.${section}`, { defaultValue: '' });
  };

  return (
    <div className="console-settings-search">
      <span className="console-search">
        <span className="console-search__icon" aria-hidden="true">
          <SearchIcon />
        </span>
        <input
          type="search"
          className="clav-input console-search__input"
          role="combobox"
          aria-label={t('settings.search.label')}
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open ? optionId(active) : undefined}
          placeholder={t('settings.search.placeholder')}
          autoComplete="off"
          spellCheck={false}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setActive(0);
          }}
          onKeyDown={onKeyDown}
        />
      </span>
      <div id={listId} role="listbox" aria-label={t('settings.search.results')} className="console-settings-search__results" hidden={!open}>
        {open
          ? results.map((doc, index) => (
              // A button carries the option, as in the command palette: interactive by nature, so it needs no hand-added
              // key handler, and `tabIndex={-1}` keeps focus in the search box, which drives the list.
              <button
                key={doc.id}
                type="button"
                id={optionId(index)}
                role="option"
                aria-selected={index === active}
                tabIndex={-1}
                className="console-settings-search__result"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => choose(doc)}
                onPointerMove={() => setActive(index)}
              >
                <span className="console-settings-search__label">{doc.label}</span>
                <span className="console-settings-search__where">
                  {[doc.groupLabel, sectionOf(doc)].filter((part) => part !== '').join(' · ')}
                </span>
              </button>
            ))
          : null}
      </div>
      <p className="console-settings-search__none console-muted" role="status">
        {searching && results.length === 0 ? t('settings.search.none', { query: query.trim() }) : ''}
      </p>
    </div>
  );
}
